import { supabase, invokeFunction } from '@/lib/supabase';
import { parseInvokeErrorBody } from '@/lib/invoke-error';
import type { Invitation, InvitationCreateInput } from '@/types';
import type { InvitableAccessLevel } from '@/constants/accessLevels';

/** Invitation enriched with the household's display name for the UI. */
export type InvitationWithHousehold = Invitation & {
  household_name: string | null;
};

export type InviteeOnboardingStep =
  | 'demo1'
  | 'demo2'
  | 'demo3'
  | 'profile'
  | 'welcome';

export interface InvitationPreviewData {
  id: string;
  invitee_email: string;
  role: 'CO_ADMIN' | 'EDITOR' | 'VIEWER';
  status: 'PENDING' | 'ACCEPTED';
  expires_at: string | null;
  /**
   * Invite lifetime as the server words it ("24 hours") — for the expired
   * copy when this link outlives the page (YAR-371). Absent on older servers.
   */
  expiry_label?: string | null;
  onboarding_step: InviteeOnboardingStep | null;
  household_id: string;
  household_name: string | null;
  inviter: {
    display_name: string | null;
    avatar_emoji: string | null;
    avatar_color: string | null;
    avatar_url: string | null;
  } | null;
}

export type InvitationPreview =
  | { state: 'unavailable' }
  | {
      state: 'expired';
      /**
       * How long invite links last, already worded ("24 hours") by the
       * server's `formatExpiry` — the same label the invitation email uses
       * (YAR-371). `null` when the server omitted it or sent junk.
       */
      expiry_label: string | null;
    }
  | { state: 'completed' }
  | {
      state: 'pending' | 'accepted';
      invitation: InvitationPreviewData;
    };

function isInvitationPreview(value: unknown): value is InvitationPreview {
  if (typeof value !== 'object' || value === null || !('state' in value)) {
    return false;
  }
  if (
    value.state === 'unavailable' ||
    value.state === 'expired' ||
    value.state === 'completed'
  ) {
    return true;
  }
  return (
    (value.state === 'pending' || value.state === 'accepted') &&
    'invitation' in value &&
    typeof value.invitation === 'object' &&
    value.invitation !== null
  );
}

/** Narrows the optional lifetime label; anything but a non-blank string is dropped. */
function readExpiryLabel(value: object): string | null {
  if (!('expiry_label' in value) || typeof value.expiry_label !== 'string') {
    return null;
  }
  const label = value.expiry_label.trim();
  return label.length > 0 ? label : null;
}

export async function previewInvitation(
  token: string,
): Promise<InvitationPreview> {
  const { data, error } = await invokeFunction('invitations-preview', {
    body: { token },
  });

  if (error) {
    const body = await parseInvokeErrorBody(error);
    if (body?.state === 'unavailable') {
      return { state: 'unavailable' };
    }
    throw error;
  }
  if (!isInvitationPreview(data)) {
    throw new Error('Invalid invitation preview response');
  }
  if (data.state === 'expired') {
    return { state: 'expired', expiry_label: readExpiryLabel(data) };
  }
  return data;
}

export async function setInvitationOnboardingStep(
  invitationId: string,
  step: InviteeOnboardingStep,
): Promise<void> {
  const { error } = await supabase.rpc('set_invitation_onboarding_step', {
    p_invitation_id: invitationId,
    p_step: step,
  });
  if (error) throw error;
}

export async function completeInvitationOnboarding(
  invitationId: string,
): Promise<void> {
  const { error } = await supabase.rpc('complete_invitation_onboarding', {
    p_invitation_id: invitationId,
  });
  if (error) throw error;
}

export async function getMyInvitations(): Promise<InvitationWithHousehold[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Join the household name so the in-app notification can say *which*
  // household is inviting — RLS policy `households_invitee_read` lets
  // the invitee read households where they have a PENDING invitation.
  const { data, error } = await supabase
    .from('caregiver_invitations')
    .select('*, households:household_id(name)')
    .eq('invitee_email', user.email!)
    .eq('status', 'PENDING')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []).map((row) => {
    const { households, ...rest } = row as Invitation & {
      households?: { name: string | null } | null;
    };
    return {
      ...(rest as Invitation),
      household_name: households?.name ?? null,
    };
  });
}

/**
 * Invitations that still need something from the signed-in user: PENDING ones
 * they have not answered, AND ones they already ACCEPTED but never finished
 * onboarding for.
 *
 * The second half is what `getMyInvitations` (the notification bell) must not
 * return — an accepted invitation is no longer a notification — but is exactly
 * what the onboarding entry point needs. Accepting seats the member
 * immediately, several carousel steps before onboarding completes, so an
 * invitee who closes the tab mid-flow comes back holding a real membership and
 * an unfinished invitation. Without this they resolve to no invitation at all
 * and get served ADMIN onboarding, where they would create their own household
 * instead of returning to the one they just joined.
 *
 * `caregiver_invitations_invitee_read` is scoped by invitee_email with no
 * status filter, so both halves are readable under the invitee's own RLS.
 *
 * An ACCEPTED row counts only while the caller still holds an ACTIVE seat in
 * that household — the same guard `complete_invitation_onboarding` applies
 * server-side. The row matches by email, and the email outlives the seat:
 * an admin reset or an account purge deletes the users row (YAR-471), a
 * household admin can remove a member mid-carousel. Without the seat check
 * the leftover acceptance served the invitee carousel for a household the
 * account no longer belonged to, with placeholder copy and a "Next" that
 * could only fail (YAR-472, YAR-438). `my_household_ids()` is the RLS
 * helper itself, so "seated" here means exactly what it means to the
 * database.
 */
export async function getMyUnfinishedInvitations(): Promise<
  InvitationWithHousehold[]
> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // `status IN (PENDING, ACCEPTED) AND onboarding_completed_at IS NULL` rather
  // than an OR of two compound clauses. It is the same set: only
  // complete_invitation_onboarding writes that column and it requires an
  // ACCEPTED row, so a PENDING invitation's timestamp is always null. DECLINED
  // and EXPIRED drop out on status, finished invitations on the timestamp.
  const { data, error } = await supabase
    .from('caregiver_invitations')
    .select('*, households:household_id(name)')
    .eq('invitee_email', user.email!)
    .in('status', ['PENDING', 'ACCEPTED'])
    .is('onboarding_completed_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  const rows = (data ?? []).map((row) => {
    const { households, ...rest } = row as Invitation & {
      households?: { name: string | null } | null;
    };
    return {
      ...(rest as Invitation),
      household_name: households?.name ?? null,
    };
  });

  // Only ACCEPTED rows need the seat check; skip the round trip otherwise.
  if (!rows.some((row) => row.status === 'ACCEPTED')) return rows;

  // Not swallowed: a failed lookup would either strand a seated invitee in
  // admin onboarding or keep serving the orphan. The hook above fails open
  // as a whole (empty list → admin flow), which is the documented fallback.
  const { data: seated, error: seatErr } =
    await supabase.rpc('my_household_ids');
  if (seatErr) throw seatErr;
  const seatedIn = new Set<string | null>(seated ?? []);

  // household_id is nullable in the row type (SET NULL on household delete);
  // a null never matches a seat, which is the right answer for that row too.
  return rows.filter(
    (row) => row.status !== 'ACCEPTED' || seatedIn.has(row.household_id),
  );
}

export async function declineInvitation(
  invitationId: string,
): Promise<Invitation> {
  // The caregiver_invitations_invitee_decline RLS policy allows only a
  // PENDING → DECLINED transition for the invitee's own row; scoping by
  // `status = 'PENDING'` also guards against double-declines. If RLS
  // hides the row we'd get `{ data: null, error: null }` with 0 rows,
  // so surface that as a concrete error instead of silently "succeeding".
  const { data, error } = await supabase
    .from('caregiver_invitations')
    .update({ status: 'DECLINED' as const })
    .eq('id', invitationId)
    .eq('status', 'PENDING')
    .select()
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error('Invitation not found or already processed');
  }
  return data;
}

/**
 * Thrown when `household-invitations` saved the invitation but its email did
 * not go out (YAR-326). The link travels only in that email, so the invitee
 * cannot use the invitation until it is sent again — and sending again is
 * safe: the server reconciles the existing pending row and mails a fresh link.
 * `.message` is the copy the UI should surface.
 */
export class InvitationEmailNotSentError extends Error {
  readonly code = 'INVITATION_EMAIL_NOT_SENT' as const;
  constructor() {
    super(
      'The invitation was saved, but we couldn’t send the email. Please try again.',
    );
    this.name = 'InvitationEmailNotSentError';
  }
}

export async function createInvitation(
  householdId: string,
  input: InvitationCreateInput,
): Promise<void> {
  const { data, error } = await invokeFunction('household-invitations', {
    body: { household_id: householdId, ...input },
  });

  if (error) throw error;
  // Only an explicit `false` means the email failed: a server that predates
  // the field (or an empty body) keeps today's behaviour.
  const body: unknown = data;
  if (
    typeof body === 'object' &&
    body !== null &&
    'email_sent' in body &&
    body.email_sent === false
  ) {
    throw new InvitationEmailNotSentError();
  }
}

export async function getPendingByHousehold(
  householdId: string,
): Promise<Invitation[]> {
  const { data, error } = await supabase
    .from('caregiver_invitations')
    .select()
    .eq('household_id', householdId)
    .eq('status', 'PENDING')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/**
 * Thrown when `invitations-accept` rejects with a 409 ALREADY_IN_HOUSEHOLD
 * (YAR-269: Yaruta enforces at most one household per account, prospective —
 * only NEW accept attempts are blocked, existing multi-household rows are
 * left alone). `.message` is the exact copy the UI should surface; callers
 * that just do `err instanceof Error ? err.message : ...` (e.g. Activity's
 * accept handler) get the right copy for free without special-casing this
 * error.
 */
export class AlreadyInHouseholdError extends Error {
  readonly code = 'ALREADY_IN_HOUSEHOLD' as const;
  constructor() {
    super(
      'You already belong to a household. Yaruta supports one household per account.',
    );
    this.name = 'AlreadyInHouseholdError';
  }
}

export async function acceptInvitation(invitationId: string): Promise<void> {
  const { error } = await invokeFunction('invitations-accept', {
    body: { id: invitationId },
  });

  if (error) {
    // FunctionsHttpError hides the 409 body behind error.context — parse it
    // so ALREADY_IN_HOUSEHOLD gets a typed error with clear, user-facing
    // copy instead of the raw supabase-js error surfacing generically.
    const body = await parseInvokeErrorBody(error);
    if (body && body.code === 'ALREADY_IN_HOUSEHOLD') {
      throw new AlreadyInHouseholdError();
    }
    throw error;
  }
}

/**
 * Change the access level a still-pending invitation will grant.
 *
 * Backs the pencil on Settings → Household members → "Invite Sent": the
 * inviter picked the wrong level and would otherwise have to revoke and
 * re-invite (burning the magic link already sitting in the invitee's inbox).
 *
 * Direct table UPDATE, exactly like `revokeInvitation`'s DELETE, and gated by
 * the same policy — `caregiver_invitations_manager_write` is FOR ALL to
 * managers only, so a non-manager's write is rejected server-side. `role` is
 * typed `InvitableAccessLevel`, which excludes PRIMARY, so ownership can
 * never be handed out through this path; `invitations-accept` re-coerces
 * anything unexpected regardless.
 *
 * Scoped to `status = 'PENDING'` and count-checked so an invite that was
 * accepted or revoked in the meantime surfaces an error instead of silently
 * reporting success.
 */
export async function updateInvitationRole(
  invitationId: string,
  role: InvitableAccessLevel,
): Promise<void> {
  const { count, error } = await supabase
    .from('caregiver_invitations')
    .update({ role }, { count: 'exact' })
    .eq('id', invitationId)
    .eq('status', 'PENDING');
  if (error) throw error;
  if (!count) {
    throw new Error('Invitation not found or already processed');
  }
}

/**
 * Revoke a pending invitation from the inviter side. Deletes the row
 * outright so any magic link still sitting in the invitee's inbox stops
 * working — `invitations-accept` will simply not find the row. We use
 * DELETE rather than a status update because `invitation_status` has no
 * `REVOKED` value, and because a revoked invite has no history we ever
 * need to read back in the UI.
 *
 * Uses `{ count: 'exact' }` + a zero-row check so the caller can surface
 * a concrete error when the invite was already accepted, already
 * revoked, or hidden by RLS — instead of silently claiming success.
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  const { count, error } = await supabase
    .from('caregiver_invitations')
    .delete({ count: 'exact' })
    .eq('id', invitationId)
    .eq('status', 'PENDING');
  if (error) throw error;
  // `count` is typed `number | null` — treat both 0 and null as not-deleted
  // so an absent count header cannot silently look like success.
  if (!count) {
    throw new Error('Invitation not found or already processed');
  }
}
